import { Modal, Form, Input, Select, Button, message, Tag, Space, Empty, Radio } from 'antd';
import { useState, useEffect } from 'react';
import { api } from '../utils/request';
import { ReloadOutlined } from '@ant-design/icons';

const { TextArea } = Input;

interface Ticket {
  id: string;
  title: string;
  content: string;
  category: number;
  status: number;
  priority: number;
  createTime: string;
  updateTime: string;
}

interface SupportTicketModalProps {
  visible: boolean;
  onClose: () => void;
}

const statusMap: Record<number, { color: string; text: string }> = {
  0: { color: 'default', text: 'Pending' },
  1: { color: 'processing', text: 'Processing' },
  2: { color: 'success', text: 'Resolved' },
  3: { color: 'error', text: 'Closed' },
};

const priorityMap: Record<number, string> = {
  1: 'Low',
  2: 'Medium',
  3: 'High',
  4: 'Urgent',
};

const categoryColorMap: Record<number, string> = {
  1: 'blue',
  2: 'purple',
  3: 'orange',
  4: 'green',
  5: 'cyan',
  6: 'default',
};

const categoryTextMap: Record<number, string> = {
  1: 'Drawing',
  2: '3D Model',
  3: 'Invoice',
  4: 'Payment',
  5: 'SDK',
  6: 'Other',
};

const categoryOptions = [
  { label: 'Drawing', value: 1 },
  { label: '3D Model Library', value: 2 },
  { label: 'Invoice', value: 3 },
  { label: 'Payment', value: 4 },
  { label: 'SDK', value: 5 },
  { label: 'Other', value: 6 },
];

export default function SupportTicketModal({ visible, onClose }: SupportTicketModalProps) {
  const [form] = Form.useForm();
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState<'list' | 'create'>('list');

  useEffect(() => {
    if (visible) {
      loadTickets();
    }
  }, [visible]);

  const loadTickets = async () => {
    setLoading(true);
    try {
      const res = await api.get<{ records: Ticket[] }>(`/support/tickets?page=1&pageSize=100`);
      const data = res as any;
      setTickets(data.records || []);
    } catch (error) {
      console.error('Failed to load tickets:', error);
      setTickets([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      await api.post('/support/tickets', values);
      message.success('Ticket submitted successfully');
      form.resetFields();
      setActiveTab('list');
      loadTickets();
    } catch (error) {
      message.error('Failed to submit ticket');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title="Support Center"
      open={visible}
      onCancel={onClose}
      footer={null}
      width={700}
      className="support-ticket-modal"
    >
      <div className="mt-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex gap-2">
            <Button
              type={activeTab === 'list' ? 'primary' : 'default'}
              onClick={() => setActiveTab('list')}
            >
              My Tickets
            </Button>
            <Button
              type={activeTab === 'create' ? 'primary' : 'default'}
              onClick={() => setActiveTab('create')}
            >
              New Ticket
            </Button>
          </div>
          <Button icon={<ReloadOutlined />} onClick={loadTickets} loading={loading}>
            Refresh
          </Button>
        </div>

        {activeTab === 'list' ? (
          <div className="max-h-96 overflow-y-auto">
            {tickets.length === 0 ? (
              <Empty description="No tickets yet" className="my-8">
                <Button type="primary" onClick={() => setActiveTab('create')}>
                  Submit your first ticket
                </Button>
              </Empty>
            ) : (
              <div className="space-y-3">
                {tickets.map((item) => (
                  <div key={item.id} className="p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-sm truncate flex-1">{item.title}</span>
                      <Tag color={statusMap[item.status]?.color} className="flex-shrink-0 ml-2 text-xs">
                        {statusMap[item.status]?.text}
                      </Tag>
                    </div>
                    <p className="text-gray-500 text-xs line-clamp-2 mb-2">{item.content}</p>
                    <div className="flex items-center justify-between">
                      <Space size={4}>
                        {item.category && (
                          <Tag color={categoryColorMap[item.category]} className="text-xs">
                            {categoryTextMap[item.category]}
                          </Tag>
                        )}
                        <Tag className="text-xs">{priorityMap[item.priority]}</Tag>
                      </Space>
                      <span className="text-xs text-gray-400">
                        {new Date(item.createTime).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <Form form={form} layout="vertical" className="mt-4">
            <Form.Item
              label="Title"
              name="title"
              rules={[{ required: true, message: 'Please enter a title' }]}
            >
              <Input placeholder="Brief description of your issue" />
            </Form.Item>
            <Form.Item
              label="Category"
              name="category"
              rules={[{ required: true, message: 'Please select a category' }]}
            >
              <Radio.Group options={categoryOptions} />
            </Form.Item>
            <Form.Item
              label="Priority"
              name="priority"
              rules={[{ required: true, message: 'Please select priority' }]}
              initialValue={2}
            >
              <Select>
                <Select.Option value={1}>Low</Select.Option>
                <Select.Option value={2}>Medium</Select.Option>
                <Select.Option value={3}>High</Select.Option>
                <Select.Option value={4}>Urgent</Select.Option>
              </Select>
            </Form.Item>
            <Form.Item
              label="Description"
              name="content"
              rules={[{ required: true, message: 'Please describe your issue' }]}
            >
              <TextArea
                rows={6}
                placeholder="Please describe your issue in detail..."
              />
            </Form.Item>
            <div className="flex justify-end gap-2">
              <Button onClick={() => setActiveTab('list')}>Cancel</Button>
              <Button type="primary" onClick={handleSubmit} loading={submitting}>
                Submit Ticket
              </Button>
            </div>
          </Form>
        )}
      </div>
    </Modal>
  );
}
